import { useCallback } from "react";
import {
  type Airport,
  type AirportRegion,
  type City,
  type FlightAlertKey,
  RegionType,
  type TripContext,
} from "@b2bportal/air-price-watch-api";
import type {
  EventTransformerHooks,
  UseInstantTransformEvent,
} from "@b2bportal/core-tracking";
import { ensureExhaustive } from "@overrides/utilities";
import {
  type AirPriceWatchHTSEvent,
  AirPriceWatchCommonTrackingEventsName,
  type AirPriceWatchProperties,
  type ExpiredWatch,
  type TappedWatch,
  type ViewWatches,
  type WatchEntry,
  type AddedWatch,
  type RemovedWatch,
} from "./events";
import {
  type AirPriceWatchAddedEvent,
  type AirPriceWatchesLoadedEvent,
  type AirPriceWatchesViewedEvent,
  type AirPriceWatchExpiredEvent,
  type AirPriceWatchRawEvent,
  AirPriceWatchRawEventName,
  type AirPriceWatchRemovedEvent,
  type AirPriceWatchTappedEvent,
} from "./types";

const extractLocationDetails = (
  airport: AirportRegion,
  tripContext: TripContext
):
  | {
      code: string;
      country_code: string;
      country_group: string;
      label: string;
      region_code: string;
    }
  | undefined => {
  switch (airport.regionType) {
    case RegionType.airport: {
      const airportInfo: Airport | null = tripContext.airports[airport.code];
      return {
        region_code: airportInfo?.geography.regionCode ?? airport.code,
        code: airport.code,
        country_code: airportInfo?.geography.countryCode ?? airport.code,
        country_group: airportInfo?.geography.timeZone ?? airport.code,
        label: airportInfo?.cityName ?? airport.code,
      };
    }
    case RegionType.city: {
      const cityInfo: City | null = tripContext.cities[airport.code];
      return {
        region_code: cityInfo?.geography.regionCode ?? airport.code,
        code: airport.code,
        country_code: cityInfo?.geography.countryCode ?? airport.code,
        country_group: cityInfo?.geography.timeZone ?? airport.code,
        label: cityInfo.name ?? airport.code,
      };
    }
    default:
      ensureExhaustive(airport.regionType);
  }
};

const extractAirPriceWatchCommonProperties = (
  flightKey: FlightAlertKey,
  tripContext: TripContext
): AirPriceWatchProperties => {
  const destinationDetails = extractLocationDetails(
    flightKey.destination,
    tripContext
  );
  const originDetails = extractLocationDetails(flightKey.origin, tripContext);
  return {
    departure_date: flightKey.departureDate,
    destination: destinationDetails?.label,
    destination_code: destinationDetails?.code,
    destination_country_code: destinationDetails?.country_code,
    destination_country_grouping: destinationDetails?.country_group,
    destination_label: destinationDetails?.label,
    destination_region_code: destinationDetails?.code,
    return_date: flightKey.returnDate,
    origin: originDetails?.label,
    origin_country_code: originDetails?.code,
    origin_country_grouping: originDetails?.country_group,
    origin_label: originDetails?.label,
    origin_region_code: originDetails?.region_code,
  };
};

export const useAirPriceWatchAddedTransformer: UseInstantTransformEvent<
  AirPriceWatchAddedEvent,
  AddedWatch
> = () => {
  return useCallback(() => {
    const name: AirPriceWatchCommonTrackingEventsName.added_watch =
      AirPriceWatchCommonTrackingEventsName.added_watch;
    const htsEvent = {
      name,
      properties: {},
    };

    return htsEvent;
  }, []);
};

export const useAirPriceWatchRemovedTransformer: UseInstantTransformEvent<
  AirPriceWatchRemovedEvent,
  RemovedWatch
> = () => {
  return useCallback(() => {
    const name: AirPriceWatchCommonTrackingEventsName.removed_watch =
      AirPriceWatchCommonTrackingEventsName.removed_watch;
    const htsEvent = {
      name,
      properties: {},
    };

    return htsEvent;
  }, []);
};

export const useAirPriceWatchExpiredTransformer: UseInstantTransformEvent<
  AirPriceWatchExpiredEvent,
  ExpiredWatch
> = () => {
  return useCallback(() => {
    const name: AirPriceWatchCommonTrackingEventsName.expired_watch =
      AirPriceWatchCommonTrackingEventsName.expired_watch;
    const htsEvent = {
      name,
      properties: {},
    };

    return htsEvent;
  }, []);
};

export const useAirPriceWatchTappedTransformer: UseInstantTransformEvent<
  AirPriceWatchTappedEvent,
  TappedWatch
> = () => {
  return useCallback((event) => {
    const name: AirPriceWatchCommonTrackingEventsName.tapped_watch =
      AirPriceWatchCommonTrackingEventsName.tapped_watch;
    const { watch, tripContext } = event.properties;
    const flightKey: FlightAlertKey = watch.key.value;
    const htsEvent = {
      name,
      properties: {
        ...extractAirPriceWatchCommonProperties(flightKey, tripContext),
      },
    };

    return htsEvent;
  }, []);
};

export const useAirPriceWatchesViewedTransformer: UseInstantTransformEvent<
  AirPriceWatchesViewedEvent,
  ViewWatches
> = () => {
  return useCallback((event) => {
    const name: AirPriceWatchCommonTrackingEventsName.view_watches =
      AirPriceWatchCommonTrackingEventsName.view_watches;
    const htsEvent = {
      name,
      properties: {
        watches_active: event.properties.active_watches,
      },
    };

    return htsEvent;
  }, []);
};

export const useAirPriceWatchesLoadedTransformer: UseInstantTransformEvent<
  AirPriceWatchesLoadedEvent,
  WatchEntry
> = () => {
  return useCallback((event) => {
    const name: AirPriceWatchCommonTrackingEventsName.watch_entry =
      AirPriceWatchCommonTrackingEventsName.watch_entry;
    const htsEvent = {
      name,
      properties: {
        watches_active: event.properties.active_watches,
      },
    };

    return htsEvent;
  }, []);
};

export const AIR_PRICE_WATCH_HTS_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  AirPriceWatchRawEvent,
  AirPriceWatchHTSEvent
> = {
  [AirPriceWatchRawEventName.AirPriceWatchAdded]: [
    useAirPriceWatchAddedTransformer,
  ],
  [AirPriceWatchRawEventName.AirPriceWatchExpired]: [
    useAirPriceWatchExpiredTransformer,
  ],
  [AirPriceWatchRawEventName.AirPriceWatchRemoved]: [
    useAirPriceWatchRemovedTransformer,
  ],
  [AirPriceWatchRawEventName.AirPriceWatchTapped]: [
    useAirPriceWatchTappedTransformer,
  ],
  [AirPriceWatchRawEventName.AirPriceWatchesLoaded]: [
    useAirPriceWatchesLoadedTransformer,
  ],
  [AirPriceWatchRawEventName.AirPriceWatchesViewed]: [
    useAirPriceWatchesViewedTransformer,
  ],
};
